import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { API_BASE_URL, IStoredUser, TokenStorageService } from '@dlap-ui/core';
import { delay, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DASHBOARD_LABELS,
  PANEL_LABELS,
  ROLE_DASHBOARDS,
  VIEW_METRICS,
  VIEW_PANELS,
} from './operations-dashboard.catalogue';
import {
  applicationLifecycle,
  DEEDS_OFFICE_ROLES,
  holderRole,
  isEntryStatus,
  isTerminalStatus,
} from './operations-dashboard.lifecycle';
import {
  DashboardColumn,
  DashboardConfig,
  DashboardDetail,
  DashboardFilters,
  DashboardMetric,
  DashboardPanel,
  DashboardResult,
  DashboardRow,
  DashboardView,
  OPERATIONS_DASHBOARD_PATH,
} from './operations-dashboard.models';

const AS_OF = '2026-10-14T12:00:00+02:00';
const DAY = 86400000;
const SERVICES = [
  'DEED_RE_ISSUANCE',
  'DEED_TRANSFER',
  'DEED_SUBDIVISION',
  'DEED_OF_GRANT',
  'LAND_TENURE_TRANSFER',
  'LAND_TENURE_SUBDIVISION',
  'BOND_REGISTRATION',
];
const STAGES = [
  ...new Set(
    SERVICES.flatMap((type) =>
      applicationLifecycle(type).steps.map((step) => step.role),
    ),
  ),
];
const STATUSES = [
  ...new Set([
    ...SERVICES.flatMap((type) => applicationLifecycle(type).sequence),
    'RESUBMITTED',
    'QUERIED',
  ]),
];
const CHANNELS = ['BANK', 'PAYNOW', 'ZIMSWITCH'];
const label = (id: string) =>
  id
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
const options = (ids: string[]) => ids.map((id) => ({ id, label: label(id) }));
const columns = (...keys: string[]): DashboardColumn[] =>
  keys.map((key) => ({ key, label: label(key) }));
const date = (daysAgo: number) =>
  new Date(Date.parse(AS_OF) - daysAgo * DAY).toISOString().slice(0, 10);

interface CaseRecord {
  id: string;
  serviceType: string;
  officeId: string;
  lawFirmId: string;
  userId: string;
  currentStatus: string;
  assignmentSource: 'APPROVAL_TASK' | 'APPLICATION_COLUMN';
  submittedAt: string;
  paidAt: string;
  approvedAt: string;
  completedAt: string;
  queriedAt: string;
  stage: string;
  resubmitted: boolean;
  amount: number;
  currency: string;
  channel: string;
  transactionStatus: string;
  invoice: string;
}
const userProfile = (userId: string) => {
  const n = Number(userId.replace('mock-user-', '')) || 1;
  return {
    id: userId,
    name: label(userId.replace('mock-user-', 'Example officer ')),
    role: DEEDS_OFFICE_ROLES[(n - 1) % DEEDS_OFFICE_ROLES.length],
    officeId: n % 3 === 0 ? 'mock-bulawayo' : 'mock-harare',
  };
};
function activeStaff(records: CaseRecord[], f: DashboardFilters) {
  const handled = new Map<string, number>();
  records.forEach((r) => handled.set(r.userId, (handled.get(r.userId) ?? 0) + 1));
  return [...handled]
    .map(([userId, applications]) => ({ ...userProfile(userId), applications }))
    .filter((u) => !f.officeId || u.officeId === f.officeId);
}
const CASES: CaseRecord[] = Array.from({ length: 420 }, (_, i) => {
  const age = i % 75;
  const serviceType = SERVICES[i % SERVICES.length];
  const life = applicationLifecycle(serviceType);
  const reachedFinal = i % 4 === 0 && age >= 8;
  const stepIndex = i % (life.sequence.length - 1);
  const currentStatus = reachedFinal
    ? life.terminalStatus
    : stepIndex === 0 &&
        i % 11 === 0 &&
        life.entryStatuses.includes('RESUBMITTED')
      ? 'RESUBMITTED'
      : life.sequence[stepIndex];
  return {
    id: `MOCK-APP-${String(i + 1).padStart(4, '0')}`,
    serviceType,
    officeId: i % 3 === 0 ? 'mock-bulawayo' : 'mock-harare',
    lawFirmId: `mock-firm-${(i % 4) + 1}`,
    userId: `mock-user-${(i % 8) + 1}`,
    currentStatus,
    assignmentSource: i % 3 === 1 ? 'APPLICATION_COLUMN' : 'APPROVAL_TASK',
    submittedAt: date(age),
    paidAt: date(Math.max(0, age - 1)),
    approvedAt: date(Math.max(0, age - 3)),
    completedAt: i % 4 === 0 && age >= 8 ? date(age - 8) : '',
    queriedAt: i % 9 === 0 && age >= 4 ? date(age - 4) : '',
    stage: holderRole(serviceType, currentStatus),
    resubmitted: i % 11 === 0,
    amount: [40, 50, 65, 80][i % 4],
    currency: i % 5 === 0 ? 'ZWG' : 'USD',
    channel: CHANNELS[i % 3],
    transactionStatus:
      i % 13 === 0 ? 'FAILED' : i % 17 === 0 ? 'PENDING' : 'SUCCESS',
    invoice: `MOCK-INV-${String(i + 1).padStart(4, '0')}`,
  };
});

export function mockDashboardConfig(user: IStoredUser | null): DashboardConfig {
  const views = Object.keys(DASHBOARD_LABELS) as DashboardView[];
  const defaultView =
    (user?.roles ?? [])
      .map((role) => ROLE_DASHBOARDS[role]?.[0])
      .find((id): id is DashboardView => !!id) ?? views[0];
  const global = (user?.roles ?? []).some((role) =>
    [
      'ADMIN',
      'PRODUCT_OWNER',
      'CHIEF_REGISTRAR',
      'DEPUTY_CHIEF_REGISTRAR',
      'CHIEF_ACCOUNTANT',
    ].includes(role),
  );
  const officeId =
    user?.officeId === 'mock-bulawayo' ? 'mock-bulawayo' : 'mock-harare';
  return {
    views: views.map((id) => ({ id, label: DASHBOARD_LABELS[id] })),
    defaultView,
    offices: [
      { id: 'mock-harare', label: 'Harare' },
      { id: 'mock-bulawayo', label: 'Bulawayo' },
    ].filter((o) => global || o.id === officeId),
    serviceTypes: options(SERVICES),
    stages: options(STAGES),
    lifecycles: SERVICES.map((type) => {
      const life = applicationLifecycle(type);
      return {
        applicationType: type,
        label: label(type),
        entryStatuses: life.entryStatuses,
        terminalStatus: life.terminalStatus,
      };
    }),
    currencies: options(['USD', 'ZWG']),
    lawFirms: Array.from({ length: 4 }, (_, i) => ({
      id: `mock-firm-${i + 1}`,
      label: `Example law firm ${i + 1}`,
    })),
    users: Array.from({ length: 8 }, (_, i) => ({
      id: `mock-user-${i + 1}`,
      label: `Example officer ${i + 1}`,
    })),
    statuses: options([...STATUSES, 'SUCCESS', 'FAILED', 'PENDING']),
    paymentChannels: options(CHANNELS),
    lockedOfficeId: global ? null : officeId,
    timezone: 'Africa/Harare',
    asOf: AS_OF,
    mock: true,
  };
}
function status(r: CaseRecord, to: string): string {
  const life = applicationLifecycle(r.serviceType);
  if (r.completedAt && r.completedAt <= to) return life.terminalStatus;
  if (r.queriedAt && r.queriedAt <= to) return 'QUERIED';
  return r.currentStatus === life.terminalStatus
    ? life.sequence[life.sequence.length - 2]
    : r.currentStatus;
}
function isCompleted(r: CaseRecord, to: string) {
  return isTerminalStatus(r.serviceType, status(r, to));
}
function isNew(r: CaseRecord, to: string) {
  return isEntryStatus(r.serviceType, status(r, to));
}
function inPeriod(value: string, f: DashboardFilters) {
  return !!value && value >= f.from && value <= f.to;
}
function filteredCases(view: DashboardView, f: DashboardFilters) {
  return CASES.filter(
    (r) =>
      r.submittedAt <= f.to &&
      (!f.officeId || r.officeId === f.officeId) &&
      (!f.serviceType?.length || f.serviceType.includes(r.serviceType)) &&
      (!f.stage || r.stage === f.stage) &&
      (!f.lawFirmId || r.lawFirmId === f.lawFirmId) &&
      (!f.userId || r.userId === f.userId) &&
      (!f.paymentChannel || r.channel === f.paymentChannel) &&
      (!f.invoiceNumber || r.invoice === f.invoiceNumber) &&
      (!f.status ||
        status(r, f.to) === f.status ||
        r.transactionStatus === f.status) &&
      (view !== 'registrar' || r.stage === 'REGISTRAR') &&
      (view !== 'finance' || r.stage === 'FINANCE_OFFICER'),
  );
}
function group(
  records: CaseRecord[],
  key: (r: CaseRecord) => string,
  value: (r: CaseRecord) => number = () => 1,
): { id: string; label: string; value: number }[] {
  const groups = new Map<string, number>();
  records.forEach((r) => {
    const k = key(r);
    groups.set(k, (groups.get(k) ?? 0) + value(r));
  });
  return [...groups]
    .map(([id, v]) => ({ id, label: label(id.replace('mock-', '')), value: v }))
    .sort((a, b) => b.value - a.value);
}
const sum = (records: CaseRecord[]) =>
  records.reduce((n, r) => n + r.amount, 0);
const caseRows = (records: CaseRecord[], f: DashboardFilters): DashboardRow[] =>
  records.map((r) => ({
    id: r.id,
    reference: r.id,
    service_type: label(r.serviceType),
    office: label(r.officeId.replace('mock-', '')),
    stage: isCompleted(r, f.to) ? 'Completed' : label(r.stage),
    status: label(status(r, f.to)),
    officer: label(r.userId.replace('mock-user-', 'Example officer ')),
    assignment_source:
      r.assignmentSource === 'APPROVAL_TASK' ? 'Approval task' : 'Application column',
    submitted: r.submittedAt,
  }));
const CASE_COLUMNS = columns(
  'reference',
  'service_type',
  'office',
  'stage',
  'status',
  'officer',
  'assignment_source',
  'submitted',
);
const PAYMENT_COLUMNS = columns(
  'invoice',
  'office',
  'payment_channel',
  'status',
  'amount',
  'currency',
  'paid_at',
);
const paymentRows = (records: CaseRecord[]): DashboardRow[] =>
  records.map((r) => ({
    id: r.invoice,
    invoice: r.invoice,
    office: label(r.officeId.replace('mock-', '')),
    payment_channel: r.channel,
    status: r.transactionStatus,
    amount: r.amount,
    currency: r.currency,
    paid_at: r.paidAt,
  }));

export function mockDashboard(
  view: DashboardView,
  f: DashboardFilters,
): DashboardResult {
  const all = filteredCases(view, f);
  const lodged = all.filter((r) => inPeriod(r.submittedAt, f));
  const completed = all.filter((r) => inPeriod(r.completedAt, f));
  const queried = all.filter(
    (r) => status(r, f.to) === 'QUERIED' && !isCompleted(r, f.to),
  );
  const approved = all.filter(
    (r) => r.transactionStatus === 'SUCCESS' && inPeriod(r.approvedAt, f),
  );
  const pending = all.filter((r) => !isCompleted(r, f.to));
  const freshApplications = all.filter((r) => isNew(r, f.to));
  const pendingInPeriod = lodged.filter((r) => !isCompleted(r, f.to));
  const held = pending.filter(
    (r) => status(r, f.to) !== 'QUERIED' && DEEDS_OFFICE_ROLES.includes(r.stage),
  );
  const payments = all.filter(
    (r) => r.currency === f.currency && inPeriod(r.paidAt, f),
  );
  const successful = payments.filter((r) => r.transactionStatus === 'SUCCESS');
  const revenue = sum(successful);
  const staffCases = filteredCases(view, { ...f, serviceType: undefined });
  const activeUsers = activeStaff(staffCases, f).length;
  const rate = (numerator: number, denominator: number) =>
    denominator ? Math.round((numerator / denominator) * 10000) / 100 : null;
  const metric = (
    id: string,
    labelText: string,
    value: number | null,
    unit: DashboardMetric['unit'] = 'count',
    scope: DashboardMetric['scope'] = 'period',
    description = 'Activity in the selected date range',
  ): DashboardMetric => ({
    id,
    label: labelText,
    value,
    unit,
    scope,
    description,
  });
  const metrics: DashboardMetric[] = [
    metric(
      'register',
      'Register total',
      all.length,
      'count',
      'as-of',
      'Applications lodged by the end date',
    ),
    metric(
      'new',
      'New applications',
      freshApplications.length,
      'count',
      'as-of',
      'Applications currently at the first status of their application type',
    ),
    metric(
      'completed',
      'Completed in period',
      completed.length,
      'count',
      'period',
      'Applications that reached the final status of their application type in the period',
    ),
    metric(
      'queried',
      'Applications queried',
      queried.length,
      'count',
      'as-of',
      'Applications currently in a queried status',
    ),
    metric('finance-approved', 'Finance approved', approved.length),
    metric(
      'in-office',
      'In the deeds office',
      held.length,
      'count',
      'as-of',
      'Open cases held by an internal workflow stage',
    ),
    metric(
      'pending',
      'Pending applications',
      pendingInPeriod.length,
      'count',
      'period',
      'Applications lodged in the period that have not reached the final status of their application type, including queried cases',
    ),
    metric(
      'active-users',
      'Active users',
      activeUsers,
      'count',
      'as-of',
      'Users with activity up to the end date',
    ),
    metric(
      'devices',
      'Devices distributed',
      all.length ? 16 : 0,
      'count',
      'as-of',
      'Active device assignments at the end date',
    ),
    metric(
      'active-offices',
      'Active offices',
      new Set(lodged.map((r) => r.officeId)).size,
    ),
    metric('searches', 'Deed searches', lodged.length * 2),
    metric('search-users', 'Deed search users', activeUsers),
    metric('revenue', 'Actual revenue', revenue, 'money'),
    metric('transactions', 'Completed transactions', successful.length),
    metric('statutory', 'Statutory fees', revenue * 0.2, 'money'),
    metric('inflow', 'Total external inflow', revenue, 'money'),
    metric('system-inflow', 'System wallet inflow', revenue * 0.6, 'money'),
    metric('user-inflow', 'User wallet inflow', revenue * 0.4, 'money'),
    metric(
      'average-transaction',
      'Average transaction',
      successful.length ? revenue / successful.length : null,
      'money',
    ),
    metric(
      'failure-rate',
      'Transaction failure rate',
      rate(
        payments.filter((r) => r.transactionStatus === 'FAILED').length,
        payments.filter((r) => r.transactionStatus !== 'PENDING').length,
      ),
      'percent',
    ),
    metric(
      'error-rate',
      'Application query rate',
      rate(
        lodged.filter((r) => r.queriedAt && r.queriedAt <= f.to).length,
        lodged.length,
      ),
      'percent',
      'period',
      'Applications lodged in the period that were queried by the end date',
    ),
  ];
  const panels: DashboardPanel[] = VIEW_PANELS[view].map((id) => {
    const p: DashboardPanel = {
      id,
      title: PANEL_LABELS[id],
      description:
        'Activity in the selected date range. Select a row for details.',
      kind: 'bars',
      unit: 'count',
      scope: 'period',
      columns: columns('label', 'value'),
      rows: [],
      totalElements: 0,
    };
    const bars = (
      rows: DashboardRow[],
      unit: DashboardMetric['unit'] = 'count',
    ) => {
      p.rows = rows;
      p.unit = unit;
    };
    const table = (rows: DashboardRow[], cols: DashboardColumn[]) => {
      p.kind = 'table';
      p.rows = rows;
      p.columns = cols;
    };
    const snapshot = () => {
      p.scope = 'as-of';
      p.description =
        'Snapshot at the selected end date. Select a row for details.';
    };
    switch (id) {
      case 'portal-workload':
      case 'workflow':
        snapshot();
        bars(group(held, (r) => r.stage));
        break;
      case 'finance-queue': {
        snapshot();
        table(
          group(
            held.filter((r) => r.stage === 'FINANCE_OFFICER'),
            (r) => r.userId,
          ).map((g) => {
            const mine = held.filter(
              (r) => r.stage === 'FINANCE_OFFICER' && r.userId === g.id,
            );
            return {
              id: g.id,
              label: g.label,
              value: mine.length,
              officer: g.label,
              first_time: mine.filter((r) => !r.resubmitted).length,
              resubmitted: mine.filter((r) => r.resubmitted).length,
              total: mine.length,
            };
          }),
          columns('officer', 'first_time', 'resubmitted', 'total'),
        );
        p.kind = 'stacked-bars';
        p.series = [
          { key: 'first_time', label: 'First-time' },
          { key: 'resubmitted', label: 'Resubmitted' },
        ];
        break;
      }
      case 'pending-status':
        snapshot();
        bars(group(pending, (r) => status(r, f.to)));
        break;
      case 'status-counts':
        snapshot();
        table(
          group(all, (r) => `${r.serviceType}|${status(r, f.to)}`).map((g) => ({
            id: g.id,
            service: label(g.id.split('|')[0]),
            status: label(g.id.split('|')[1]),
            count: g.value,
          })),
          columns('service', 'status', 'count'),
        );
        break;
      case 'assigned':
        snapshot();
        table(
          group(
            held,
            (r) => `${r.serviceType}|${r.stage}|${r.userId}|${r.officeId}`,
          ).map((g) => {
            const [service, stage, user, office] = g.id.split('|');
            return {
              id: g.id,
              service: label(service),
              stage: label(stage),
              officer: label(user.replace('mock-user-', 'Example officer ')),
              office: label(office.replace('mock-', '')),
              applications: g.value,
            };
          }),
          columns('service', 'stage', 'officer', 'office', 'applications'),
        );
        break;
      case 'application-trend':
        bars(group(lodged, (r) => r.submittedAt));
        p.rows.sort((a, b) => a.id.localeCompare(b.id));
        p.kind = 'line';
        break;
      case 'law-firm-applications':
        bars(group(lodged, (r) => r.lawFirmId));
        break;
      case 'office-applications':
        bars(group(lodged, (r) => r.officeId));
        break;
      case 'service-applications':
        bars(group(lodged, (r) => r.serviceType));
        break;
      case 'application-stages':
        snapshot();
        table(
          group(held, (r) => `${r.serviceType}|${r.stage}`).map((g) => ({
            id: g.id,
            service: label(g.id.split('|')[0]),
            stage: label(g.id.split('|')[1]),
            count: g.value,
          })),
          columns('service', 'stage', 'count'),
        );
        break;
      case 'bottlenecks':
        snapshot();
        bars(
          group(
            held,
            (r) => r.stage,
            (r) =>
              Math.max(0, (Date.parse(f.to) - Date.parse(r.submittedAt)) / DAY),
          ),
          'days',
        );
        p.description = 'Total waiting days across open cases at each stage.';
        break;
      case 'backlog':
        snapshot();
        bars(
          group(pending, (r) => {
            const age = (Date.parse(f.to) - Date.parse(r.submittedAt)) / DAY;
            return age < 8
              ? '0–7 days'
              : age < 31
                ? '8–30 days'
                : age < 61
                  ? '31–60 days'
                  : '61+ days';
          }),
        );
        break;
      case 'processing-times':
      case 'user-processing-times': {
        const keys = [
          ...new Set(
            completed.map((r) =>
              id === 'processing-times' ? r.serviceType : r.userId,
            ),
          ),
        ];
        bars(
          keys.map((key) => {
            const mine = completed.filter(
              (r) =>
                (id === 'processing-times' ? r.serviceType : r.userId) === key,
            );
            return {
              id: key,
              label: label(key),
              value:
                mine.reduce(
                  (n, r) =>
                    n +
                    (Date.parse(r.completedAt) - Date.parse(r.submittedAt)) /
                      DAY,
                  0,
                ) / mine.length,
            };
          }),
          'days',
        );
        break;
      }
      case 'error-rates':
        bars(
          [...new Set(lodged.map((r) => r.serviceType))].map((key) => {
            const cohort = lodged.filter((r) => r.serviceType === key);
            return {
              id: key,
              label: label(key),
              value: rate(
                cohort.filter((r) => r.queriedAt && r.queriedAt <= f.to).length,
                cohort.length,
              ),
            };
          }),
          'percent',
        );
        p.description =
          'Share of applications lodged in the period that were queried by the end date.';
        break;
      case 'efficiency':
        bars(
          [
            {
              id: 'completed',
              label: 'Completed / submitted in period',
              value: rate(completed.length, lodged.length),
            },
          ],
          'percent',
        );
        p.description =
          'Throughput ratio; may exceed 100% when older backlog is completed.';
        break;
      case 'training':
        snapshot();
        bars(
          all.length
            ? [
                { id: 'trained', label: 'Trained firms', value: 3 },
                { id: 'untrained', label: 'Awaiting training', value: 1 },
              ]
            : [],
        );
        break;
      case 'device-distribution':
        snapshot();
        bars(group(all, (r) => r.lawFirmId).map((g) => ({ ...g, value: 4 })));
        break;
      case 'fee-revenue':
      case 'statutory-details':
        bars(
          group(
            successful,
            (r) => `${r.serviceType}_FEE`,
            (r) => r.amount * (id === 'statutory-details' ? 0.2 : 1),
          ),
          'money',
        );
        break;
      case 'user-revenue':
        bars(
          group(
            successful,
            (r) => r.userId,
            (r) => r.amount,
          ),
          'money',
        );
        break;
      case 'payment-channels':
        table(
          group(payments, (r) => r.channel).map((g) => {
            const mine = payments.filter((r) => r.channel === g.id);
            return {
              id: g.id,
              channel: g.label,
              successful: mine.filter((r) => r.transactionStatus === 'SUCCESS')
                .length,
              failed: mine.filter((r) => r.transactionStatus === 'FAILED')
                .length,
              pending: mine.filter((r) => r.transactionStatus === 'PENDING')
                .length,
              revenue: sum(
                mine.filter((r) => r.transactionStatus === 'SUCCESS'),
              ),
            };
          }),
          columns('channel', 'successful', 'failed', 'pending', 'revenue'),
        );
        break;
      case 'bank-status':
        bars(group(payments, (r) => r.transactionStatus));
        break;
      case 'wallet-usage':
        bars(
          successful.length
            ? [
                { id: 'system', label: 'System funds', value: revenue * 0.6 },
                { id: 'user', label: 'User funds', value: revenue * 0.4 },
              ]
            : [],
          'money',
        );
        break;
      case 'system-usage':
      case 'user-usage':
        bars(
          group(
            successful,
            (r) => r.serviceType,
            (r) => r.amount * (id === 'system-usage' ? 0.6 : 0.4),
          ),
          'money',
        );
        break;
      case 'fee-audit':
        snapshot();
        table(
          [...new Set(all.map((r) => r.serviceType))].map((key, i) => ({
            id: `${key}_FEE`,
            fee_type: `${key}_FEE`,
            description: `${label(key)} fee`,
            price: [40, 50, 65, 80][i % 4],
            currency: f.currency,
            updated_at: '2026-09-01',
            updated_by: 'Example fee administrator',
          })),
          columns(
            'fee_type',
            'description',
            'price',
            'currency',
            'updated_at',
            'updated_by',
          ),
        );
        break;
      case 'transaction-details':
      case 'bank-payments':
        table(paymentRows(payments), PAYMENT_COLUMNS);
        break;
      case 'finance-activity':
        table(paymentRows(successful), PAYMENT_COLUMNS);
        break;
      case 'alerts':
        snapshot();
        table(
          pending
            .filter(
              (r) => (Date.parse(f.to) - Date.parse(r.submittedAt)) / DAY > 60,
            )
            .slice(0, 3)
            .map((r) => ({
              id: r.id,
              severity: 'Warning',
              message: `${r.id} has waited more than 60 days`,
              office: label(r.officeId.replace('mock-', '')),
            })),
          columns('severity', 'message', 'office'),
        );
        break;
      case 'user-actions':
      case 'recent-activity':
        table(caseRows(lodged, f), CASE_COLUMNS);
        break;
    }
    p.totalElements = p.rows.length;
    return p;
  });
  return {
    view,
    asOf: AS_OF,
    timezone: 'Africa/Harare',
    currency: f.currency,
    mock: true,
    filters: f,
    metrics: metrics.filter((m) => VIEW_METRICS[view].includes(m.id)),
    panels,
  };
}

const APPLICATION_DETAIL_WIDGETS = [
  'register',
  'new',
  'pending',
  'queried',
  'completed',
  'finance-approved',
  'in-office',
];
export function mockDetails(
  view: DashboardView,
  f: DashboardFilters,
  widget: string,
  groupKey: string | undefined,
  pageNumber: number,
  pageSize: number,
  search?: string,
): DashboardDetail {
  const dashboard = mockDashboard(view, f);
  const panel = dashboard.panels.find((p) => p.id === widget);
  let records = filteredCases(view, f);
  const needle = (search ?? '').trim().toLowerCase();
  if (needle)
    records = records.filter(
      (r) =>
        r.id.toLowerCase().includes(needle) ||
        r.invoice.toLowerCase().includes(needle),
    );
  if (widget === 'active-users') {
    const staff = activeStaff(
      filteredCases(view, { ...f, serviceType: undefined }),
      f,
    ).filter((u) => !needle || u.name.toLowerCase().includes(needle));
    const officeIds = f.officeId
      ? [f.officeId]
      : ['mock-harare', 'mock-bulawayo'];
    const roleIds = [
      ...DEEDS_OFFICE_ROLES,
      ...new Set(staff.map((u) => u.role)),
    ].filter((id, i, all) => all.indexOf(id) === i);
    const userRows: DashboardRow[] = staff.map((u) => ({
      id: u.id,
      user: u.name,
      role: label(u.role),
      office: label(u.officeId.replace('mock-', '')),
      applications_handled: u.applications,
    }));
    return {
      title: 'Active users',
      matrix: {
        title: 'Active users per office and role',
        rowHeader: 'Office',
        columns: roleIds.map((id) => ({ id, label: label(id) })),
        rows: officeIds.map((id) => {
          const mine = staff.filter((u) => u.officeId === id);
          return {
            id,
            label: label(id.replace('mock-', '')),
            values: Object.fromEntries(
              roleIds.map((role) => [
                role,
                mine.filter((u) => u.role === role).length,
              ]),
            ),
            total: mine.length,
          };
        }),
        columnTotals: Object.fromEntries(
          roleIds.map((role) => [
            role,
            staff.filter((u) => u.role === role).length,
          ]),
        ),
        total: staff.length,
      },
      columns: columns('user', 'role', 'office', 'applications_handled'),
      content: userRows.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
      totalElements: userRows.length,
      pageNumber,
      pageSize,
    };
  }
  let rows: DashboardRow[];
  let cols: DashboardColumn[];
  const grouped = (key: (r: CaseRecord) => string) => {
    if (groupKey) records = records.filter((r) => key(r) === groupKey);
  };
  if (
    panel &&
    [
      'fee-audit',
      'training',
      'device-distribution',
      'alerts',
    ].includes(widget)
  ) {
    rows = panel.rows.filter((r) => !groupKey || r.id === groupKey);
    cols = panel.columns;
  } else {
    const snapshotWidgets = [
      'register',
      'new',
      'queried',
      'in-office',
      'portal-workload',
      'workflow',
      'finance-queue',
      'pending-status',
      'status-counts',
      'assigned',
      'application-stages',
      'bottlenecks',
      'backlog',
    ];
    const paymentWidgets = [
      'revenue',
      'transactions',
      'statutory',
      'inflow',
      'system-inflow',
      'user-inflow',
      'average-transaction',
      'failure-rate',
      'fee-revenue',
      'user-revenue',
      'payment-channels',
      'bank-status',
      'wallet-usage',
      'system-usage',
      'user-usage',
      'bank-payments',
      'transaction-details',
      'statutory-details',
      'finance-activity',
    ];
    const financial = paymentWidgets.includes(widget);
    if (financial) {
      records = records.filter(
        (r) => r.currency === f.currency && inPeriod(r.paidAt, f),
      );
      if (
        ![
          'failure-rate',
          'payment-channels',
          'bank-status',
          'bank-payments',
          'transaction-details',
        ].includes(widget)
      )
        records = records.filter((r) => r.transactionStatus === 'SUCCESS');
      if (['fee-revenue', 'statutory-details'].includes(widget))
        grouped((r) => `${r.serviceType}_FEE`);
      if (['system-usage', 'user-usage'].includes(widget))
        grouped((r) => r.serviceType);
      if (widget === 'user-revenue') grouped((r) => r.userId);
      if (widget === 'payment-channels') grouped((r) => r.channel);
      if (widget === 'bank-status') grouped((r) => r.transactionStatus);
      if (
        groupKey &&
        ['bank-payments', 'transaction-details', 'finance-activity'].includes(
          widget,
        )
      )
        grouped((r) => r.invoice);
      rows = paymentRows(records);
      cols = PAYMENT_COLUMNS;
      const allocation = ['statutory', 'statutory-details'].includes(widget)
        ? 0.2
        : ['system-inflow', 'system-usage'].includes(widget) ||
            (widget === 'wallet-usage' && groupKey === 'system')
          ? 0.6
          : ['user-inflow', 'user-usage'].includes(widget) ||
              (widget === 'wallet-usage' && groupKey === 'user')
            ? 0.4
            : 1;
      if (allocation !== 1) {
        rows = rows.map((r) => ({
          ...r,
          allocated_amount: Number(r['amount']) * allocation,
        }));
        cols = [...cols, ...columns('allocated_amount')];
      }
    } else {
      if (
        [
          'completed',
          'processing-times',
          'user-processing-times',
          'efficiency',
        ].includes(widget)
      )
        records = records.filter((r) => inPeriod(r.completedAt, f));
      else if (widget === 'queried')
        records = records.filter(
          (r) => status(r, f.to) === 'QUERIED' && !isCompleted(r, f.to),
        );
      else if (widget === 'finance-approved')
        records = records.filter(
          (r) => r.transactionStatus === 'SUCCESS' && inPeriod(r.approvedAt, f),
        );
      else if (!snapshotWidgets.includes(widget))
        records = records.filter((r) => inPeriod(r.submittedAt, f));
      if (widget === 'new')
        records = records.filter((r) => isNew(r, f.to));
      if (['pending', 'pending-status', 'backlog'].includes(widget))
        records = records.filter((r) => !isCompleted(r, f.to));
      if (
        [
          'in-office',
          'portal-workload',
          'workflow',
          'finance-queue',
          'assigned',
          'application-stages',
          'bottlenecks',
        ].includes(widget)
      )
        records = records.filter(
          (r) =>
            !isCompleted(r, f.to) &&
            status(r, f.to) !== 'QUERIED' &&
            DEEDS_OFFICE_ROLES.includes(r.stage),
        );
      if (widget === 'finance-queue') {
        records = records.filter((r) => r.stage === 'FINANCE_OFFICER');
        grouped((r) => r.userId);
      }
      if (['portal-workload', 'workflow', 'bottlenecks'].includes(widget))
        grouped((r) => r.stage);
      if (widget === 'pending-status') grouped((r) => status(r, f.to));
      if (widget === 'status-counts')
        grouped((r) => `${r.serviceType}|${status(r, f.to)}`);
      if (widget === 'assigned')
        grouped((r) => `${r.serviceType}|${r.stage}|${r.userId}|${r.officeId}`);
      if (widget === 'application-stages')
        grouped((r) => `${r.serviceType}|${r.stage}`);
      if (widget === 'application-trend') grouped((r) => r.submittedAt);
      if (widget === 'law-firm-applications') grouped((r) => r.lawFirmId);
      if (widget === 'office-applications') grouped((r) => r.officeId);
      if (
        ['service-applications', 'processing-times', 'error-rates'].includes(
          widget,
        )
      )
        grouped((r) => r.serviceType);
      if (widget === 'error-rate')
        records = records.filter((r) => r.queriedAt && r.queriedAt <= f.to);
      if (widget === 'user-processing-times') grouped((r) => r.userId);
      if (widget === 'backlog')
        grouped((r) => {
          const age = (Date.parse(f.to) - Date.parse(r.submittedAt)) / DAY;
          return age < 8
            ? '0–7 days'
            : age < 31
              ? '8–30 days'
              : age < 61
                ? '31–60 days'
                : '61+ days';
        });
      if (groupKey && ['user-actions', 'recent-activity'].includes(widget))
        grouped((r) => r.id);
      rows = caseRows(records, f);
      cols = CASE_COLUMNS;
    }
  }
  const officeIds = f.officeId ? [f.officeId] : ['mock-harare', 'mock-bulawayo'];
  const breakdown =
    APPLICATION_DETAIL_WIDGETS.includes(widget)
      ? {
          title: 'Applications per office',
          items: officeIds.map((id) => ({
            id,
            label: label(id.replace('mock-', '')),
            value: records.filter((r) => r.officeId === id).length,
          })),
          total: rows.length,
        }
      : undefined;
  return {
    title:
      panel?.title ??
      dashboard.metrics.find((m) => m.id === widget)?.label ??
      widget,
    breakdown,
    columns: cols,
    content: rows.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    totalElements: rows.length,
    pageNumber,
    pageSize,
  };
}

/** Remove this interceptor or set dashboardMocks=false to send identical requests to the gateway. */
export const operationsDashboardMockInterceptor: HttpInterceptorFn = (
  req,
  next,
) => {
  const base =
    inject(API_BASE_URL).replace(/\/$/, '') + OPERATIONS_DASHBOARD_PATH;
  if (
    !environment.dashboardMocks ||
    req.method !== 'GET' ||
    !req.url.startsWith(base + '/')
  )
    return next(req);
  const config = mockDashboardConfig(inject(TokenStorageService).getUser());
  const path = req.url.slice(base.length + 1).split('/');
  const fail = (status: number, message: string) =>
    throwError(
      () =>
        new HttpErrorResponse({
          status,
          url: req.urlWithParams,
          error: { success: false, message },
        }),
    );
  if (!config.views.length)
    return fail(403, 'No dashboard access for this account');
  if (path[0] === 'config')
    return of(
      new HttpResponse({
        status: 200,
        body: {
          success: true,
          status: 'OK',
          code: 200,
          message: 'Synthetic dashboard data',
          data: config,
        },
      }),
    ).pipe(delay(180));
  const view = path[0] as DashboardView;
  if (!config.views.some((v) => v.id === view))
    return fail(403, 'Dashboard access denied');
  const f: DashboardFilters = {
    from: req.params.get('from') ?? '',
    to: req.params.get('to') ?? '',
    currency: req.params.get('currency') ?? 'USD',
  };
  const dates = [f.from, f.to];
  if (
    dates.some(
      (v) =>
        !/^\d{4}-\d{2}-\d{2}$/.test(v) ||
        !Number.isFinite(Date.parse(v)) ||
        new Date(v).toISOString().slice(0, 10) !== v,
    ) ||
    f.from > f.to ||
    f.to > AS_OF.slice(0, 10)
  )
    return fail(400, 'Invalid date range');
  for (const key of [
    'officeId',
    'stage',
    'lawFirmId',
    'userId',
    'status',
    'paymentChannel',
    'invoiceNumber',
  ] as const) {
    const v = req.params.get(key);
    if (v) f[key] = v;
  }
  const serviceTypes = req.params.getAll('serviceType') ?? [];
  if (serviceTypes.length) f.serviceType = serviceTypes;
  if (
    serviceTypes.some((v) => !config.serviceTypes.some((o) => o.id === v))
  )
    return fail(400, 'Unknown filter');
  if (
    config.lockedOfficeId &&
    f.officeId &&
    config.lockedOfficeId !== f.officeId
  )
    return fail(403, 'Office access denied');
  f.officeId = config.lockedOfficeId ?? f.officeId;
  const allowed: [string | undefined, { id: string }[]][] = [
    [f.officeId, config.offices],
    [f.stage, config.stages],
    [f.currency, config.currencies],
    [f.lawFirmId, config.lawFirms],
    [f.userId, config.users],
    [f.status, config.statuses],
    [f.paymentChannel, config.paymentChannels],
  ];
  if (
    allowed.some(([value, list]) => value && !list.some((o) => o.id === value))
  )
    return fail(400, 'Unknown filter');
  if (path.length > 1 && path[1] !== 'details')
    return fail(404, 'Unknown dashboard endpoint');
  const widget = req.params.get('widgetId') ?? '';
  const page = Number(req.params.get('pageNumber') ?? 1),
    size = Number(req.params.get('pageSize') ?? 10);
  if (
    path[1] === 'details' &&
    ((!VIEW_PANELS[view].includes(widget) &&
      !VIEW_METRICS[view].includes(widget)) ||
      !Number.isInteger(page) ||
      page < 1 ||
      !Number.isInteger(size) ||
      size < 1 ||
      size > 100)
  )
    return fail(400, 'Invalid details request');
  const data =
    path[1] === 'details'
      ? mockDetails(
          view,
          f,
          widget,
          req.params.get('groupKey') ?? undefined,
          page,
          size,
          (req.params.get('search') ?? '').slice(0, 64),
        )
      : mockDashboard(view, f);
  const panelId = req.params.get('panelId');
  if (panelId && path[1] !== 'details') {
    if (!VIEW_PANELS[view].includes(panelId))
      return fail(400, 'Unknown panel');
    const full = data as DashboardResult;
    return of(
      new HttpResponse({
        status: 200,
        body: {
          success: true,
          status: 'OK',
          code: 200,
          message: 'Synthetic dashboard data',
          data: {
            ...full,
            metrics: [],
            panels: full.panels.filter((p) => p.id === panelId),
          },
        },
      }),
    ).pipe(delay(180));
  }
  return of(
    new HttpResponse({
      status: 200,
      body: {
        success: true,
        status: 'OK',
        code: 200,
        message: 'Synthetic dashboard data',
        data,
      },
    }),
  ).pipe(delay(180));
};
